import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ArrowLeft, Sparkles as SparklesIcon } from "lucide-react";
import Sparkles from "../components/Sparkles";
import LangToggle from "../components/LangToggle";
import { fileUrl, getGallery, getKingdomInfo } from "../lib/api";
import { useLang, t } from "../lib/i18n";

const CASTLE_BG =
  "https://images.unsplash.com/photo-1766191395451-91c79c6ed395?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxtYWdpY2FsJTIwZmFpcnklMjB0YWxlJTIwY2FzdGxlJTIwcGluayUyMHNreXxlbnwwfHx8fDE3ODE1OTQ1OTd8MA&ixlib=rb-4.1.0&q=85";

export default function GrandFinale() {
  const lang = useLang();
  const [guests, setGuests] = useState([]);
  const [info, setInfo] = useState({ princess_name: "Anagha", princess_image: null });
  const [phase, setPhase] = useState("title");
  const [reveal, setReveal] = useState(0);

  useEffect(() => {
    Promise.all([getGallery(), getKingdomInfo()])
      .then(([g, i]) => {
        setGuests(g);
        setInfo(i);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const titleT = setTimeout(() => setPhase("mosaic"), 3500);
    return () => clearTimeout(titleT);
  }, []);

  useEffect(() => {
    if (phase !== "mosaic" || !guests.length) return;
    const tm = setInterval(() => {
      setReveal((n) => {
        if (n >= guests.length) {
          clearInterval(tm);
          setTimeout(() => setPhase("message"), 2500);
          return n;
        }
        return n + 1;
      });
    }, 250);
    return () => clearInterval(tm);
  }, [phase, guests.length]);

  const princessSrc = info.princess_image ? fileUrl(info.princess_image) : null;

  return (
    <div className="fixed inset-0 overflow-hidden bg-plum-deep text-blush" data-testid="grand-finale">
      <div
        className="absolute inset-0 animate-ken-burns"
        style={{ backgroundImage: `url(${CASTLE_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-plum-deep/50 via-plum/55 to-plum-deep/95" />
      <Sparkles count={90} />

      <Link to="/welcome" className="absolute top-5 left-5 z-30 glass rounded-full px-3 py-1.5 flex items-center gap-2 text-plum">
        <ArrowLeft size={14} /> <span className="label-eyebrow">{t("exit", lang)}</span>
      </Link>
      <div className="absolute top-5 right-5 z-30"><LangToggle light /></div>

      {phase === "title" && (
        <div className="relative w-full h-full grid place-items-center px-12 animate-[fadeIn_0.8s_ease-out]">
          <div className="text-center max-w-4xl">
            <p className="font-script text-4xl text-blush mb-2" style={{ fontFamily: "'Noto Sans Telugu','Dancing Script',cursive" }}>{t("fin_letter_to", lang)}</p>
            <h1 className="font-display text-6xl sm:text-8xl lg:text-9xl font-semibold gold-text leading-[0.95]" style={{ fontFamily: "'Tiro Telugu','Noto Serif Telugu','Yatra One','Eczar',serif" }}>
              {t("fin_title_a", lang)}
              <br />
              {t("fin_title_b", lang)}
            </h1>
            <div className="ornament-divider my-8 max-w-md mx-auto"><SparklesIcon size={16} /></div>
            <p className="font-script text-3xl sm:text-4xl text-blush" style={{ fontFamily: "'Noto Sans Telugu','Dancing Script',cursive" }}>— {t("df_princess", lang)}</p>
          </div>
        </div>
      )}

      {phase === "mosaic" && (
        <div className="relative w-full h-full grid place-items-center">
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 flex flex-col items-center">
            {princessSrc && (
              <div
                className="relative w-44 h-44 sm:w-56 sm:h-56 rounded-full overflow-hidden ring-4 ring-[#FCF6BA]/80 shadow-[0_0_80px_rgba(252,246,186,0.7)] animate-float"
                style={{ animationDuration: "5s" }}
              >
                <img src={princessSrc} alt={info.princess_name} className="w-full h-full object-cover" />
              </div>
            )}
            <p className="mt-3 font-script text-4xl text-blush drop-shadow" style={{ fontFamily: "'Noto Sans Telugu','Dancing Script',cursive" }}>{t("df_princess", lang)}</p>
            <p className="label-eyebrow text-blush/80" style={{ fontFamily: "'Noto Sans Telugu',sans-serif" }}>{t("fin_turning_one", lang)}</p>
          </div>

          {guests.slice(0, reveal).map((g, idx) => {
            const total = Math.max(guests.length, 1);
            const ring = idx < 8 ? 0 : idx < 20 ? 1 : 2;
            const inRing = ring === 0 ? Math.min(8, total) : ring === 1 ? Math.min(12, total - 8) : total - 20;
            const ringIdx = ring === 0 ? idx : ring === 1 ? idx - 8 : idx - 20;
            const angle = (ringIdx / Math.max(inRing, 1)) * Math.PI * 2 - Math.PI / 2;
            const rx = [22, 33, 42][ring];
            const ry = [25, 38, 46][ring];
            const x = 50 + Math.cos(angle) * rx;
            const y = 50 + Math.sin(angle) * ry;
            const size = ring === 0 ? "w-20 h-20 sm:w-24 sm:h-24" : ring === 1 ? "w-16 h-16 sm:w-20 sm:h-20" : "w-12 h-12 sm:w-16 sm:h-16";
            return (
              <div
                key={g.id}
                className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center animate-[finaleAppear_0.6s_ease-out_both]"
                style={{ left: `${x}%`, top: `${y}%` }}
              >
                <div className={`${size} rounded-full overflow-hidden ring-2 ring-[#FCF6BA]/70 shadow-lg`}>
                  <img src={fileUrl(g.character_path)} alt={g.name} className="w-full h-full object-cover" />
                </div>
                <p className="mt-1 font-script text-xs sm:text-sm text-blush drop-shadow text-center max-w-[120px] truncate">{g.name}</p>
              </div>
            );
          })}
        </div>
      )}

      {phase === "message" && (
        <div className="relative w-full h-full grid place-items-center px-12 animate-[fadeIn_1.2s_ease-out]">
          <div className="text-center max-w-3xl">
            <div className="flex justify-center mb-4">
              <Heart className="text-[#FFC4D6]" size={48} fill="#FFC4D6" />
            </div>
            <p
              className="font-display text-3xl sm:text-5xl text-blush leading-relaxed italic"
              style={{ fontFamily: "'Tiro Telugu','Noto Serif Telugu','Yatra One','Eczar',serif" }}
              lang={lang === "te" ? "te" : "en"}
            >
              &ldquo;{t("fin_message", lang)}&rdquo;
            </p>
            <p className="font-script text-5xl sm:text-7xl gold-text mt-8" style={{ fontFamily: "'Noto Sans Telugu','Dancing Script',cursive" }}>— {info.princess_name} ✦</p>
            <button
              onClick={() => { setPhase("title"); setReveal(0); }}
              className="btn-ghost-royal mt-12 text-blush border-blush/40 hover:bg-white/10"
              data-testid="finale-restart-btn"
              style={{ fontFamily: "'Noto Sans Telugu',sans-serif" }}
            >
              {t("fin_replay", lang)}
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(20px) } to { opacity: 1; transform: translateY(0) } }
        @keyframes finaleAppear {
          from { opacity: 0; transform: translate(-50%, -50%) scale(0.5) }
          to { opacity: 1; transform: translate(-50%, -50%) scale(1) }
        }
      `}</style>
    </div>
  );
}
